import {
  Box,
  CircularProgress,
  IconButton,
  Modal,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useSwipeable } from "react-swipeable";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Magnifier from "../customComponents/Magnifier";
import CloseIcon from "@mui/icons-material/Close";
import { useDispatch, useSelector } from "react-redux";
import { isSignedUrlExpired } from "../../functions/helperFunctions";
import { getPermitPackages } from "../../store/actions/dataActions";

const ReviewCreatedFormsModal = ({ open, setOpen, docNotaryTypeValue }) => {
  const { selectedRow } = useSelector((state) => state.myPermitsComponent);
  const { magnifierOn } = useSelector((state) => state.buildingState);

  const [activeStep, setActiveStep] = useState(0);
  const [filteredDocuments, setFilteredDocuments] = useState([]);
  const [formIndex, setFormIndex] = useState(0);
  const [totalImages, setTotalImages] = useState(0);
  const [offset, setOffset] = useState([]);
  const [urlExpiredLoading, setUrlExpiredLoading] = useState(false);

  const dispatch = useDispatch();

  useEffect(() => {
    const filterDocs = Object.entries(selectedRow?.documents || {}).filter(
      ([key, _]) => {
        if (docNotaryTypeValue === 0) return true; // Include all documents
        if (docNotaryTypeValue === 1)
          return key === "noc" || key === "noc_affidavit"; // Only "noc" documents
        if (docNotaryTypeValue === 2) return key !== "noc"; // Exclude "noc" documents
        return false;
      }
    );

    setFilteredDocuments(filterDocs);

    // Flatten the array of signed URLs from all filtered documents
    const totalImgs = filterDocs.reduce((count, [, doc]) => {
      return count + (doc?.signed?.length || 0);
    }, 0);

    setTotalImages(totalImgs);

    const calculatedOffset = [];

    filterDocs?.flatMap(([_, doc], docIndex) => {
      doc?.signed?.forEach(() => {
        calculatedOffset.push(docIndex);
      });
      return null;
    });

    setOffset(calculatedOffset);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    const handleUpdatePackage = async () => {
      setUrlExpiredLoading(true);

      await dispatch(getPermitPackages());

      setUrlExpiredLoading(false);
    };

    if (filteredDocuments) {
      const currentDoc = filteredDocuments[formIndex];

      const urls = currentDoc?.[1]?.signed;

      urls?.forEach((url) => {
        const isExpiredUrl = isSignedUrlExpired(url);

        if (isExpiredUrl) {
          handleUpdatePackage();

          return;
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formIndex, dispatch, open]);

  const handleNext = () => {
    if (magnifierOn) return;

    const newStep = activeStep + 1;

    if (activeStep < totalImages - 1) {
      setActiveStep(newStep);
      setFormIndex(offset[newStep]);
    }
  };

  const handleBack = () => {
    if (magnifierOn) return;

    const newStep = activeStep - 1;

    if (activeStep > 0) {
      setActiveStep(newStep);
      setFormIndex(offset[newStep]);
    }
  };

  const handlers = useSwipeable({
    onSwipedLeft: magnifierOn ? null : handleNext,
    onSwipedRight: magnifierOn ? null : handleBack,
    preventScrollOnSwipe: true,
    trackMouse: magnifierOn ? false : true,
  });

  return (
    <Modal
      open={open}
      onClose={() => {
        setOpen(false);
        setActiveStep(0);
        setFormIndex(0);
      }}
      sx={{ overflow: "auto" }}
    >
      <Box
        sx={{
          overflowY: "auto",
          position: "relative",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          height: "95%",
          maxWidth: "70rem",
          bgcolor: "background.paper",
          borderRadius: "18px",
          boxShadow: 24,
          px: 4,
          py: 2,
          animation: "fadeIn 0.5s ease-in-out",
        }}
      >
        <Box sx={{ position: "relative" }} {...handlers}>
          <Box sx={{ position: "absolute", right: 10, top: 10, zIndex: 999 }}>
            <IconButton
              onClick={() => {
                setOpen(false);
                setActiveStep(0);
                setFormIndex(0);
                setTotalImages(0);
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <Box sx={{ cursor: "default" }}>
            <Box sx={{ width: "100%", overflow: "hidden" }}>
              {filteredDocuments?.flatMap(([key, doc], docIndex) => {
                const selectedDocument = selectedRow.selected_documents?.find(
                  (item) => item.name === key
                );

                const label = selectedDocument?.label;

                // Calculate cumulative offset for the current document
                const cumulativeOffset = filteredDocuments
                  .slice(0, docIndex) // Take all previous documents
                  .reduce(
                    (count, [, prevDoc]) =>
                      count + (prevDoc?.signed?.length || 0),
                    0
                  );

                const sortMap = {
                  "_B.jpg": 1,
                  "_C.jpg": 2,
                  "_D.jpg": 3,
                  "_E.jpg": 4,
                  "_F.jpg": 5,
                };

                const sortedUrls = Array(doc?.signed?.length)
                  .fill("")
                  .map((_, i) => "");

                doc?.signed?.forEach((url) => {
                  // Check if the URL contains any of the sortMap keys
                  const foundKey = Object.keys(sortMap).find((key) =>
                    url.includes(key)
                  );

                  // If a key was found, use its value as index in sortedUrls array
                  if (foundKey) {
                    const index = sortMap[foundKey];

                    // Ensure the array has enough length to avoid undefined values
                    sortedUrls.splice(index, index, url);
                    // sortedUrls[index] = url;
                  } else {
                    // If no key was found, add to the end of the array
                    sortedUrls.splice(0, 1, url);
                  }
                });

                return doc?.signed?.length > 0
                  ? sortedUrls?.map((url, index) =>
                      activeStep === cumulativeOffset + index ? ( // Compare with cumulative index
                        (<React.Fragment key={`${docIndex}-${index}`}>
                          <Box
                            sx={{
                              width: "100%",
                              zIndex: 999,
                              display: "flex",
                              justifyContent: "center",
                            }}
                          >
                            <Typography variant="h5" color="primary">
                              {label} Form
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              width: "100%",
                              zIndex: 999,
                              display: "flex",
                              justifyContent: "center",
                            }}
                          >
                            <Typography variant="h6" color="primary">
                              Document {formIndex + 1} of{" "}
                              {
                                filteredDocuments?.filter(
                                  ([_, doc]) => doc?.signed?.length,
                                ).length
                              }
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              width: "100%",
                              zIndex: 999,
                              display: "flex",
                              justifyContent: "center",
                            }}
                          >
                            <Typography
                              variant="body2"
                              fontWeight="bold"
                              color="primary"
                            >
                              (Page {index + 1} of {doc?.signed?.length})
                            </Typography>
                          </Box>
                          <Box
                            key={`${docIndex}-${index}`}
                            sx={{
                              p: 2,
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              maxHeight: "83vh",
                              minHeight: "83vh",
                            }}
                          >
                            {urlExpiredLoading ? (
                              <CircularProgress />
                            ) : (
                              <Magnifier
                                src={url}
                                lensSize={300}
                                zoomFactor={1}
                                customStyle={{
                                  maxWidth: "100%", // Restrict width to 100% of container
                                  maxHeight: "82vh", // Restrict height to 75% of the viewport height
                                  objectFit: "contain", // Ensure image scales properly
                                  boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
                                }}
                              />
                            )}
                          </Box>
                        </React.Fragment>)
                      ) : null
                    )
                  : null;
              })}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <Typography sx={{ fontWeight: "bold" }} color="primary">
                  Left click and hold over any part of the document to use a
                  magnifying glass to inspect the document
                </Typography>
              </Box>
            </Box>
          </Box>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: 0,
              display: activeStep === 0 ? "none" : undefined,
            }}
          >
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                handleBack(e);
              }}
            >
              <ArrowBackIcon sx={{ fontSize: "40px" }} />
            </IconButton>
          </Box>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              right: 0,
              display:
                totalImages - 1 === activeStep || totalImages === 0
                  ? "none"
                  : undefined,
            }}
          >
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                handleNext();
              }}
            >
              <ArrowForwardIcon sx={{ fontSize: "40px" }} />
            </IconButton>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default React.memo(ReviewCreatedFormsModal);
